import { EXPRESSION_EVAL } from "../constants/expressionBuilder.constants";

/**
 * @param {*} value - Value to inspect
 * @returns {boolean} True if value looks like a calendar date (not arbitrary text)
 * @author Abhishek Dhillon
 */
const isDateLike = (value) => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (typeof value !== "string") {
    return false;
  }
  const s = value.trim();
  if (EXPRESSION_EVAL.ISO_DATE_ONLY.test(s) || EXPRESSION_EVAL.ISO_DATE_TIME_PREFIX.test(s)) {
    return !Number.isNaN(Date.parse(s));
  }
  return false;
};

/**
 * Semantic type for typed comparisons (expression value vs runtime data)
 * @param {*} value - Value to classify
 * @returns {'nullish'|'array'|'object'|'date'|'number'|'string'|'boolean'} Category
 * @author Abhishek Dhillon
 */
const getSemanticType = (value) => {
  if (value === null || value === undefined) {
    return "nullish";
  }
  if (Array.isArray(value)) {
    return "array";
  }
  if (value instanceof Date) {
    return "date";
  }
  if (typeof value === "object") {
    return "object";
  }
  if (typeof value === "boolean") {
    return "boolean";
  }
  if (typeof value === "number") {
    return "number";
  }
  if (typeof value === "string") {
    return isDateLike(value) ? "date" : "string";
  }
  return "string";
};

/**
 * Calendar date equality ignoring time (local calendar day)
 * @param {*} a - First date
 * @param {*} b - Second date
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
const datesEqualDateOnly = (a, b) => {
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) {
    return false;
  }
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

/**
 * Compare order for dates (date-only) and numbers
 * @param {*} a - Left value
 * @param {*} b - Right value
 * @param {'date'|'number'} kind - Comparison domain
 * @returns {number} -1, 0, or 1
 * @author Abhishek Dhillon
 */
const compareOrdered = (a, b, kind) => {
  if (kind === "date") {
    const da = new Date(a instanceof Date ? a : new Date(a));
    const db = new Date(b instanceof Date ? b : new Date(b));
    if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) {
      return NaN;
    }
    da.setHours(0, 0, 0, 0);
    db.setHours(0, 0, 0, 0);
    const ta = da.getTime();
    const tb = db.getTime();
    return ta === tb ? 0 : ta < tb ? -1 : 1;
  }
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) {
    return NaN;
  }
  return na === nb ? 0 : na < nb ? -1 : 1;
};

/**
 * Display key for plain objects (dropdown options, chips)
 * @param {Object} obj - Object with optional label / name / value
 * @returns {string}
 * @author Abhishek Dhillon
 */
const getObjectMatchKey = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return "";
  }
  const key = obj.label ?? obj.name ?? obj.value;
  return key === undefined || key === null ? "" : String(key);
};

/**
 * True if two non-array objects represent the same option/chip (label/value alignment)
 * @param {Object} a - First object
 * @param {Object} b - Second object
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
const objectsSemanticallyEqual = (a, b) => {
  return getObjectMatchKey(a) === getObjectMatchKey(b) && getObjectMatchKey(a) !== "";
};

/**
 * Typed equality for rule evaluation (handles array membership when one side is an array)
 * @param {*} dataValue - Runtime value
 * @param {*} exprValue - Value from the expression rule
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
const valuesEqualTyped = (dataValue, exprValue) => {
  const td = getSemanticType(dataValue);
  const te = getSemanticType(exprValue);

  if (td === "array" && te !== "array") {
    return dataValue.some((el) => valuesEqualTyped(el, exprValue));
  }
  if (te === "array" && td !== "array") {
    return exprValue.some((el) => valuesEqualTyped(dataValue, el));
  }

  if (td !== te) {
    return false;
  }

  switch (td) {
    case "nullish":
      return dataValue == null && exprValue == null;
    case "array":
      return arraysEqualUnordered(dataValue, exprValue);
    case "object":
      return objectsSemanticallyEqual(dataValue, exprValue);
    case "date":
      return datesEqualDateOnly(dataValue, exprValue);
    case "number":
      return Number(dataValue) === Number(exprValue);
    case "string":
      return String(dataValue) === String(exprValue);
    case "boolean":
      return Boolean(dataValue) === Boolean(exprValue);
    default:
      return false;
  }
};

/**
 * True when two arrays have the same elements (order-independent; duplicates respected)
 * @param {Array} a - First array
 * @param {Array} b - Second array
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
const arraysEqualUnordered = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  const used = new Array(b.length).fill(false);
  return a.every((itemA) => {
    const matchIndex = b.findIndex(
      (itemB, index) => !used[index] && valuesEqualTyped(itemA, itemB)
    );
    if (matchIndex === -1) {
      return false;
    }
    used[matchIndex] = true;
    return true;
  });
};

/**
 * True if list contains an entry equal to item (typed)
 * @param {Array} list - Haystack
 * @param {*} item - Needle
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
const arrayContainsTyped = (list, item) => {
  if (!Array.isArray(list)) {
    return false;
  }
  return list.some((el) => valuesEqualTyped(el, item));
};

/**
 * String for CONTAINS / MATCHES_WITH (text as text; objects use label/name/value)
 * @param {*} value - Raw value
 * @returns {string}
 * @author Abhishek Dhillon
 */
const toComparableTextString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)) {
    return getObjectMatchKey(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => toComparableTextString(item))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .join(",");
  }
  return String(value);
};

/**
 * Evaluates a single rule against data (typed comparisons for equals, ranges, text, lists)
 * @param {Object} dataObject - Data to evaluate
 * @param {Object} rule - Rule configuration
 * @returns {boolean} Evaluation result
 * @author Abhishek Dhillon
 */
export const evaluateRule = (dataObject, rule) => {
  const { field, operator, value } = rule;
  const dataValue = dataObject?.[field?.id];
  const operatorId = operator?.id || operator;

  switch (operatorId) {
    case "IS_BLANK":
      return dataValue === undefined || dataValue === null || dataValue === "";
    case "IS_NOT_BLANK":
      return dataValue !== undefined && dataValue !== null && dataValue !== "";
    case "IS_ZERO":
      return dataValue === 0 || dataValue === "0";
    case "IS_NOT_ZERO":
      return dataValue !== 0 && dataValue !== "0";
  }

  if (dataValue === undefined || dataValue === null) return false;

  switch (operatorId) {
    case "EQUALS":
      return valuesEqualTyped(dataValue, value);
    case "NOT_EQUALS":
      return !valuesEqualTyped(dataValue, value);
    case "GREATER_THAN": {
      const td = getSemanticType(dataValue);
      if (td !== getSemanticType(value) || (td !== "number" && td !== "date")) {
        return false;
      }
      const c = compareOrdered(dataValue, value, td);
      return !Number.isNaN(c) && c > 0;
    }
    case "LESS_THAN": {
      const td = getSemanticType(dataValue);
      if (td !== getSemanticType(value) || (td !== "number" && td !== "date")) {
        return false;
      }
      const c = compareOrdered(dataValue, value, td);
      return !Number.isNaN(c) && c < 0;
    }
    case "GREATER_THAN_EQUAL": {
      const td = getSemanticType(dataValue);
      if (td !== getSemanticType(value) || (td !== "number" && td !== "date")) {
        return false;
      }
      const c = compareOrdered(dataValue, value, td);
      return !Number.isNaN(c) && c >= 0;
    }
    case "LESS_THAN_EQUAL": {
      const td = getSemanticType(dataValue);
      if (td !== getSemanticType(value) || (td !== "number" && td !== "date")) {
        return false;
      }
      const c = compareOrdered(dataValue, value, td);
      return !Number.isNaN(c) && c <= 0;
    }
    case "CONTAINS": {
      const needle = value
      const hay = toComparableTextString(dataValue);
      return hay.toLowerCase().includes(toComparableTextString(needle).toLowerCase());
    }
    case "MATCHES_WITH": {
      const needle = value
      return toComparableTextString(dataValue).toLowerCase() === toComparableTextString(needle).toLowerCase();
    }
    case "STARTS_WITH": {
      const needle = value
      return toComparableTextString(dataValue).toLowerCase().startsWith(toComparableTextString(needle).toLowerCase());
    }
    case "NOT_CONTAINS": {
      const needle = value
      const hay = toComparableTextString(dataValue);
      return !hay.toLowerCase().includes(toComparableTextString(needle).toLowerCase());
    }
    case "IN":
      return Array.isArray(value) ? arrayContainsTyped(value, dataValue) : false;
    case "NOT_IN":
      return Array.isArray(value) ? !arrayContainsTyped(value, dataValue) : true;
    default:
      return false;
  }
};
